// src/pages/admin/SupplierVerificationPage.jsx
// Phase 1 — M1: Admin UI for supplier GST/MSME/KYC verification

import { useState, useEffect } from 'react'
import { supplierVerificationApi } from '../../api/services'

const STATUS_COLORS = {
  not_started: 'bg-gray-100 text-gray-600',
  pending:     'bg-yellow-100 text-yellow-700',
  under_review:'bg-blue-100 text-blue-700',
  verified:    'bg-green-100 text-green-700',
  rejected:    'bg-red-100 text-red-700',
}

function KycBadge({ status }) {
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-500'}`}>
      {status?.replace('_', ' ').toUpperCase() || 'N/A'}
    </span>
  )
}

// ── Supplier row detail modal ────────────────────────────────────────────────
function SupplierKycModal({ supplier, onClose, onAction }) {
  const [notes, setNotes]     = useState('')
  const [loading, setLoading] = useState(false)
  const [result, setResult]   = useState(null)
  const profile = supplier.supplier_profile

  async function act(action) {
    if (!notes.trim() && action !== 'verifyGstin') {
      alert('Please enter notes/reason.')
      return
    }
    setLoading(true)
    try {
      let res
      if (action === 'approve')      res = await supplierVerificationApi.approve(supplier.id, { notes })
      if (action === 'reject')       res = await supplierVerificationApi.reject(supplier.id, { reason: notes })
      if (action === 'requestDocs')  res = await supplierVerificationApi.requestDocs(supplier.id, { message: notes })
      if (action === 'verifyGstin')  res = await supplierVerificationApi.verifyGstin(supplier.id)
      setResult(res.data)
      onAction()
    } catch (e) {
      alert(e.response?.data?.message || 'Action failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{supplier.business_name || supplier.name}</h2>
            <p className="text-sm text-gray-500">{supplier.email} · {supplier.gstin || 'No GSTIN'}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>

        <div className="p-6 space-y-5">
          {/* KYC Status */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-1">KYC Status</p>
              <KycBadge status={supplier.supplier_kyc_status} />
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-1">MSME Verified</p>
              <span className={`text-sm font-semibold ${profile?.msme_verified ? 'text-green-600' : 'text-gray-400'}`}>
                {profile?.msme_verified ? `✅ ${profile.msme_number}` : '—'}
              </span>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-1">GSTIN</p>
              <span className="text-sm font-mono">{supplier.gstin || '—'}</span>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-1">Documents</p>
              <span className="text-sm">{supplier.supplier_documents?.length || 0} uploaded</span>
            </div>
          </div>

          {/* Documents list */}
          {supplier.supplier_documents?.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2">Uploaded Documents</p>
              <div className="space-y-2">
                {supplier.supplier_documents.map(doc => (
                  <div key={doc.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                    <span className="text-sm text-gray-700">{doc.type_label || doc.type}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      doc.status === 'approved' ? 'bg-green-100 text-green-700' :
                      doc.status === 'rejected' ? 'bg-red-100 text-red-700' :
                      'bg-yellow-100 text-yellow-700'}`}>{doc.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* GSTIN verify button */}
          {supplier.gstin && (
            <button
              onClick={() => act('verifyGstin')}
              disabled={loading}
              className="w-full py-2 rounded-xl border border-blue-200 text-blue-700 hover:bg-blue-50 text-sm font-medium transition-colors"
            >
              {loading ? 'Verifying…' : '🔍 Verify GSTIN Online'}
            </button>
          )}

          {result && (
            <div className={`rounded-xl p-3 text-sm ${result.valid ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
              {result.message}
            </div>
          )}

          {/* Notes / reason input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes / Reason</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              placeholder="Enter approval notes or rejection reason…"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
            />
          </div>

          {/* Action buttons */}
          <div className="grid grid-cols-3 gap-3">
            <button
              onClick={() => act('approve')}
              disabled={loading}
              className="py-2.5 rounded-xl bg-green-600 text-white text-sm font-semibold hover:bg-green-700 disabled:opacity-50 transition-colors"
            >✅ Approve</button>
            <button
              onClick={() => act('reject')}
              disabled={loading}
              className="py-2.5 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50 transition-colors"
            >❌ Reject</button>
            <button
              onClick={() => act('requestDocs')}
              disabled={loading}
              className="py-2.5 rounded-xl bg-yellow-500 text-white text-sm font-semibold hover:bg-yellow-600 disabled:opacity-50 transition-colors"
            >📋 Request Docs</button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Main page ────────────────────────────────────────────────────────────────
export default function SupplierVerificationPage() {
  const [data, setData]           = useState(null)
  const [loading, setLoading]     = useState(true)
  const [filter, setFilter]       = useState('pending')
  const [search, setSearch]       = useState('')
  const [selected, setSelected]   = useState(null)

  async function load() {
    setLoading(true)
    try {
      const res = await supplierVerificationApi.list({ kyc_status: filter, search })
      setData(res.data)
    } catch {
      setData(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [filter, search])

  const counts = data?.counts || {}
  const suppliers = data?.suppliers?.data || []

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Supplier KYC Verification</h1>
      <p className="text-gray-500 mb-6">Review and approve supplier GST, MSME, and document submissions</p>

      {/* Status filter tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {[
          { key: 'pending',      label: 'Pending',      color: 'yellow' },
          { key: 'under_review', label: 'Under Review', color: 'blue'   },
          { key: 'verified',     label: 'Verified',     color: 'green'  },
          { key: 'rejected',     label: 'Rejected',     color: 'red'    },
          { key: '',             label: 'All',          color: 'gray'   },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={`px-4 py-2 rounded-xl text-sm font-semibold border transition-colors
              ${filter === tab.key
                ? 'bg-[#FF3D7F] text-white border-[#FF3D7F]'
                : 'bg-white text-gray-600 border-gray-200 hover:border-pink-300'}`}
          >
            {tab.label}
            {counts[tab.key] > 0 && (
              <span className="ml-1.5 bg-white/20 text-current rounded-full px-1.5 text-xs">
                {counts[tab.key]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="mb-4">
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, business, email, GSTIN…"
          className="w-full max-w-md border border-gray-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
        />
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-16 text-gray-400">Loading…</div>
      ) : suppliers.length === 0 ? (
        <div className="text-center py-16 text-gray-400">No suppliers found</div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs font-semibold uppercase tracking-wide">
              <tr>
                <th className="px-4 py-3 text-left">Supplier</th>
                <th className="px-4 py-3 text-left">GSTIN</th>
                <th className="px-4 py-3 text-left">KYC Status</th>
                <th className="px-4 py-3 text-left">MSME</th>
                <th className="px-4 py-3 text-left">Docs</th>
                <th className="px-4 py-3 text-left">Joined</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {suppliers.map(s => (
                <tr key={s.id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{s.business_name || s.name}</p>
                    <p className="text-gray-400 text-xs">{s.email}</p>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-600">{s.gstin || '—'}</td>
                  <td className="px-4 py-3"><KycBadge status={s.supplier_kyc_status} /></td>
                  <td className="px-4 py-3">
                    {s.msme_verified
                      ? <span className="text-green-600 text-xs font-semibold">✅ Verified</span>
                      : <span className="text-gray-300 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{s.supplier_documents_count || 0}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs">
                    {s.created_at ? new Date(s.created_at).toLocaleDateString('en-IN') : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => setSelected(s)}
                      className="px-3 py-1.5 rounded-lg bg-[#FF3D7F] text-white text-xs font-semibold hover:bg-pink-600 transition-colors"
                    >Review</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {selected && (
        <SupplierKycModal
          supplier={selected}
          onClose={() => setSelected(null)}
          onAction={() => { setSelected(null); load() }}
        />
      )}
    </div>
  )
}
